import { DocumentPermission, MentionInboxEventType } from "@src/gql/graphql";
import { z } from "zod";

export type MentionEntityType = "Whiteboard" | "Card" | "Preprint";

export function inferMentionEntityType(
	id: string,
	entityType?: string | null,
): MentionEntityType {
	if (
		entityType === "Card" ||
		entityType === "Preprint" ||
		entityType === "Whiteboard"
	) {
		return entityType;
	}
	if (id.startsWith("Card-")) {
		return "Card";
	}
	if (id.startsWith("Preprint-")) {
		return "Preprint";
	}
	return "Whiteboard";
}

export const mentionSuggestionSchema = z.object({
	id: z.string(),
	label: z.string(),
	entityType: z.string().nullish(),
	key: z.string().nullish(),
});

export const MentionSuggestionSchema = mentionSuggestionSchema;

export const mentionSuggestionsResultSchema = z.object({
	matches: z.array(mentionSuggestionSchema),
	recentlySelected: z.array(mentionSuggestionSchema),
	latestUpdated: z.array(mentionSuggestionSchema),
	sessionId: z.string(),
});

export type MentionSuggestion = z.infer<typeof mentionSuggestionSchema>;
export type MentionSuggestionsResult = z.infer<
	typeof mentionSuggestionsResultSchema
>;

export type MentionTargetType = "CARD" | "COMMENT";

export const personSuggestionSchema = z
	.object({
		id: z.string(),
		displayName: z.string(),
		email: z.string(),
		avatarUrl: z.string().nullish(),
		permission: z.nativeEnum(DocumentPermission).nullish(),
		hasAccess: z.boolean().optional(),
	})
	.transform((person) => ({
		...person,
		userId: person.id,
	}));

export type MentionPersonSuggestion = z.infer<typeof personSuggestionSchema>;

const mentionInboxItemApiSchema = z.object({
	id: z.string(),
	idempotencyId: z.string(),
	authorId: z.string(),
	authorName: z.string(),
	authorAvatarUrl: z.string().nullish(),
	sourceId: z.string(),
	anchorId: z.string().nullish(),
	snippet: z.string(),
	deepLink: z.string(),
	createdAt: z.string(),
	isRead: z.boolean(),
	readAt: z.string().nullish(),
});

export const mentionInboxItemSchema = mentionInboxItemApiSchema.transform(
	(item) => {
		const targetType: MentionTargetType = item.anchorId ? "COMMENT" : "CARD";
		return {
			...item,
			author: {
				id: item.authorId,
				displayName: item.authorName,
				avatarUrl: item.authorAvatarUrl,
			},
			targetType,
			targetTitle: item.anchorId ? "Comment" : "Card",
		};
	},
);

export type MentionInboxItem = z.infer<typeof mentionInboxItemSchema>;

export const mentionInboxPayloadSchema = z.object({
	items: z.array(mentionInboxItemSchema),
	hasMore: z.boolean(),
	nextCursor: z.string().nullish(),
});

export type MentionInboxPayload = z.infer<typeof mentionInboxPayloadSchema>;

export const recordMentionInputSchema = z.object({
	sourceId: z.string().min(1),
	anchorId: z.string().nullish(),
	mentionedUserId: z.string().min(1),
	idempotencyId: z.string().min(1),
	snippet: z.string(),
	deepLink: z.string(),
});

export type RecordMentionInput = z.infer<typeof recordMentionInputSchema>;

export const recordMentionPayloadSchema = z.object({
	mentionId: z.string().nullish(),
	success: z.boolean(),
});

export type RecordMentionPayload = z.infer<typeof recordMentionPayloadSchema>;

export const markMentionAsReadPayloadSchema = z.object({
	success: z.boolean(),
	mentionId: z.string(),
});

export type MarkMentionAsReadPayload = z.infer<
	typeof markMentionAsReadPayloadSchema
>;

export const markAllMentionsAsReadPayloadSchema = z.object({
	success: z.boolean(),
});

export type MarkAllMentionsAsReadPayload = z.infer<
	typeof markAllMentionsAsReadPayloadSchema
>;

export const mentionInboxEventTypeSchema = z.nativeEnum(MentionInboxEventType);

export const mentionInboxUpdatePayloadSchema = z.object({
	eventType: mentionInboxEventTypeSchema,
	mentionId: z.string().nullish(),
	unreadCount: z.number().int(),
	mention: mentionInboxItemSchema.nullish(),
});

export type MentionInboxUpdatePayload = z.infer<
	typeof mentionInboxUpdatePayloadSchema
>;
